import type { ReactElement } from 'react';
import { useState } from 'react';
import Dropdown from 'react-bootstrap/Dropdown';
import styled from 'styled-components';
import { Tag } from '../../../components';
import { Device } from '../../../model';
import { isOutdated } from '../version';

interface Props {
  devices?: Device[];
  updateEnabled?: boolean;
  latestLcs?: string;
  latestEms?: string;
  selectDevices: (devices: Device[]) => void;
  showUpdate: (show: boolean) => void;
  unlinkAttempt: (device: Device) => void;
}

const COLUMNS =
  '34px 110px 64px minmax(180px, 1.4fr) 120px 120px 120px minmax(140px, 1fr) 100px 30px';

const Scroll = styled.div`
  overflow-x: auto;
  padding-bottom: 4px;
`;

const Inner = styled.div`
  min-width: 1080px;
  display: flex;
  flex-direction: column;
  gap: 9px;
`;

const HeaderRow = styled.div`
  display: grid;
  grid-template-columns: ${COLUMNS};
  gap: 13px;
  align-items: center;
  padding: 2px 16px;
  font-weight:700;
  font-size: 10px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: rgba(176, 186, 209, 0.55);      
`;

const Row = styled.div`
  position: relative;
  display: grid;
  grid-template-columns: ${COLUMNS};
  gap: 13px;
  align-items: center;
  background: rgba(17, 27, 51, 0.32);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 10px;
  padding: 13px 16px;
  color: var(--white);

  .cid {
    font-family: 'JetBrains Mono', monospace;
    font-size: 12px;
    color: var(--white60);
  }

  .org .name {
    font-size: 14px;
    font-weight: 700;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .org .loc {
    margin-top: 2px;
    font-size: 12px;
    color: var(--white60);
  }

  .transfer {
    display: flex;
    flex-direction: column;
    gap: 3px;
    font-family: 'JetBrains Mono', monospace;
    font-size: 12px;
    font-weight: 600;

    i {
      font-size: 10px;
    }

    .unit {
      font-weight: 500;
      color: rgba(124, 136, 172, 0.7);
    }

    .good {
      color: rgb(127, 230, 184);
      display:flex;
      align-items:center;
      gap:5px;
    }

    .bad {
      color: rgb(255, 138, 147);
      display:flex;
      align-items:center;
      gap:5px;
    }
  }

  .next .date {
    font-size: 12px;
    font-weight: 600;
    color: rgba(205, 214, 234, 0.78)
  }

  .next .time {
    margin-top: 2px;
    font-size: 11px;
    color: rgba(176, 186, 209, 0.6);
  }

  .live {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 13px;
    font-weight: 700;
    color: rgb(127, 230, 184);
  }

  .live .dot {
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: rgb(53, 211, 153);
  }

  .muted {
    font-size: 13px;
    color: var(--white35);
  }
`;

const Accent = styled.span<{ $color: string }>`
  position: absolute;
  left: 0;
  top: 8px;
  bottom: 8px;
  width: 3px;
  border-radius: 0 3px 3px 0;
  background: ${({ $color }): string => $color};
`;

const CheckBox = styled.button<{ $checked: boolean }>`
  width: 18px;
  height: 18px;
  border-radius: 5px;
  display: grid;
  place-items: center;
  padding: 0;
  cursor: pointer;
  transition: all var(--transition);
  border: 1.5px solid
    ${({ $checked }): string => ($checked ? 'rgb(78, 95, 253)' : 'rgba(255, 255, 255, 0.2)')};
  background: ${({ $checked }): string => ($checked ? 'rgb(78, 95, 253)' : 'transparent')};
  color: ${({ $checked }): string => ($checked ? 'rgb(255, 255, 255)' : 'transparent')};

  svg {
    display: block;
  }
`;

const CheckIcon = (): ReactElement => (
  <svg viewBox='0 0 24 24' width='12' height='12' fill='none' stroke='currentColor' strokeWidth={3}>
    <path d='M5 13l4 4L19 7' />
  </svg>
);

const OptionsToggle = styled(Dropdown.Toggle)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  padding: 0;
  border-radius: 8px;
  background: transparent;
  border: none;
  color: var(--white60);

  &::after {
    content: none;
  }

  &:hover,
  &:focus {
    box-shadow: none;
    color: var(--white);
    background: rgba(255, 255, 255, 0.06);
  }
`;

const OptionsMenu = styled(Dropdown.Menu)`
  min-width: 186px;
  background: rgb(15, 24, 48);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 10px;
  box-shadow: rgba(0, 0, 0, 0.5) 0px 16px 40px;
  padding: 6px;

  .dropdown-item {
    background: transparent;
    color: rgba(213, 221, 238, 0.92);
    font-size: 13px;
    padding: 9px 11px;
    border-radius: 7px;

    &:hover,
    &:focus,
    &:active {
      background: rgba(255, 255, 255, 0.06);
      color: rgba(213, 221, 238, 0.92);
    }

    &.unlink {
      color: rgb(255, 138, 147);

      &:hover,
      &:focus,
      &:active {
        color: rgb(255, 138, 147);
        background: rgba(255, 80, 95, 0.1);
      }
    }
  }

  .dropdown-divider {
    height: 1px;
    margin: 5px 4px;
    border: none;
    background: rgba(255, 255, 255, 0.08);
  }
`;

const fmtDate = (date: Date): string =>
  date.toLocaleDateString('en', { month: '2-digit', day: '2-digit', year: '2-digit' }).replaceAll('/', '-');

const fmtTime = (date: Date): string =>
  date.toLocaleTimeString('en', { hour: '2-digit', minute: '2-digit', hour12: true });

const NextEvent = ({ device }: { device: Device }): ReactElement => {
  const next = device.nextEvent;

  if (!next) {
    return <span className='muted'>No upcoming event</span>;
  }

  const now = Date.now();
  const live = next.startTime.getTime() <= now && now <= next.endTime.getTime();

  if (live) {
    return (
      <span className='live'>
        <span className='dot cc-blink-slow' /> LIVE NOW
      </span>
    );
  }

  return (
    <div className='next'>
      <div className='date'>{fmtDate(next.startTime)}</div>
      <div className='time'>
        {fmtTime(next.startTime)} – {fmtTime(next.endTime)}
      </div>
    </div>
  );
};

const RemoteTag = ({ session }: { session: Device.Session }): ReactElement => {
  if (session === Device.Session.active) {
    return (
      <div>
        <Tag size='small' className='cc-azure' icon='fas fa-circle' iconSize={6} style={{border: 'none'}}>
          Active
        </Tag>
      </div>
    );
  }

  return (
    <div>
      <Tag size='small' className='cc-neutral' style={{border: 'none', color: 'rgba(176, 186, 209, 0.6)', background: 'rgba(255, 255, 255, 0.05)'}}>
        {session === Device.Session.inactive ? 'Inactive' : 'Offline'}
      </Tag>
    </div>
  );
};

export const DeviceTable = ({
  devices,
  updateEnabled = true,
  latestLcs,
  latestEms,
  selectDevices,
  showUpdate,
  unlinkAttempt
}: Props): ReactElement => {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  const commit = (ids: Set<string>): void => {
    setSelectedIds(ids);
    selectDevices((devices ?? []).filter((device) => ids.has(device.id)));
  };

  const toggle = (id: string): void => {
    const next = new Set(selectedIds);

    if (next.has(id)) {
      next.delete(id);
    } else {
      next.add(id);
    }

    commit(next);
  };

  const toggleAll = (checked: boolean): void => {
    commit(checked ? new Set((devices ?? []).map((device) => device.id)) : new Set());
  };

  const allSelected = !!devices?.length && selectedIds.size === devices.length;

  return (
    <Scroll className='cc-scroll'>
      <Inner>
        <HeaderRow>
          <span>
            <CheckBox
              type='button'
              $checked={allSelected}
              aria-pressed={allSelected}
              aria-label='Select all devices'
              onClick={(): void => {
                toggleAll(!allSelected);
              }}
            >
              <CheckIcon />
            </CheckBox>
          </span>
          <span>Status</span>
          <span>CID</span>
          <span>Org / Location</span>
          <span>Up / Down</span>
          <span>LCS</span>
          <span>EMS</span>
          <span>Next Event</span>
          <span>Remote</span>
          <span />
        </HeaderRow>

        {devices?.map((device) => {
          const online = device.status === Device.Status.online;
          const upGood = device.transfer.up > device.transferConfig.badUp;
          const downGood = device.transfer.down > device.transferConfig.badDown;
          const lcsOutdated = isOutdated(device.lcs, latestLcs);
          const emsOutdated = isOutdated(device.ems, latestEms);

          return (
            <Row key={device.id}>
              <Accent $color={online ? 'rgb(53, 211, 153)' : 'rgb(255, 80, 95)'} />

              <span>
                <CheckBox
                  type='button'
                  $checked={selectedIds.has(device.id)}
                  aria-pressed={selectedIds.has(device.id)}
                  aria-label='Select device'
                  onClick={(): void => {
                    toggle(device.id);
                  }}
                >
                  <CheckIcon />
                </CheckBox>
              </span>

              <span>
                <Tag
                  className={online ? 'cc-green' : 'cc-red'}
                  icon='fas fa-circle'
                  iconSize={7}
                  style={{ fontSize: '11px', border: 'none' }}
                >
                  {online ? 'ONLINE' : 'OFFLINE'}
                </Tag>
              </span>

              <span className='cid'>{device.cid}</span>

              <div className='org'>
                <div className='name'>{device.organization}</div>
                <div className='loc'>{device.location}</div>
              </div>

              <div className='transfer'>
                <span className={upGood ? 'good' : 'bad'}>
                  <i className='fas fa-arrow-up' />
                  {device.transfer.up}
                  <span className='unit'>mbps</span>
                </span>
                <span className={downGood ? 'good' : 'bad'}>
                  <i className='fas fa-arrow-down' />
                  {device.transfer.down}
                  <span className='unit'>mbps</span>
                </span>
              </div>

              <span>
                <Tag
                  size='small'
                  className={lcsOutdated ? 'cc-amber' : 'cc-green'}
                  icon={lcsOutdated ? 'fas fa-circle' : undefined}
                  iconSize={5}
                  style={{ fontFamily: "'JetBrains Mono', monospace", border: 'none' }}
                >
                  {device.lcs}
                </Tag>
              </span>

              <span>
                <Tag
                  size='small'
                  className={emsOutdated ? 'cc-amber' : 'cc-green'}
                  icon={emsOutdated ? 'fas fa-circle' : undefined}
                  iconSize={5}
                  style={{ fontFamily: "'JetBrains Mono', monospace", border: 'none' }}
                >
                  {device.ems}
                </Tag>
              </span>

              <NextEvent device={device} />

              <RemoteTag session={device.session} />

              <Dropdown alignRight>
                <OptionsToggle forwardedAs='button'>
                  <i className='far fa-ellipsis-v' />
                </OptionsToggle>
                <OptionsMenu>
                  <Dropdown.Item>Remote Access</Dropdown.Item>
                  <Dropdown.Item>Notify Client</Dropdown.Item>
                  <Dropdown.Item>Open in Cloutik</Dropdown.Item>
                  {updateEnabled && (
                    <Dropdown.Item
                      onClick={(): void => {
                        selectDevices([device]);
                        showUpdate(true);
                      }}
                    >
                      Update
                    </Dropdown.Item>
                  )}
                  <Dropdown.Divider />
                  <Dropdown.Item
                    className='unlink'
                    onClick={(): void => {
                      unlinkAttempt(device);
                    }}
                  >
                    Unlink Encoder
                  </Dropdown.Item>
                </OptionsMenu>
              </Dropdown>
            </Row>
          );
        })}
      </Inner>
    </Scroll>
  );
};
